import { Bus, Caravan, CarFront, Footprints, TrainFront } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { theme } from "./theme";
import type { Mode, Stop, TravelMode, VehicleKind } from "./types";

/**
 * Hvordan hver etappe av ruta faktisk tilbakelegges.
 *
 * Ruta har vist strekninger som streker på kartet siden starten — samme farge,
 * samme tykkelse, hele veien. Men en tur er sjelden bare kjøring: skjemaet
 * velger tog, buss eller bil for hele turen, og et par av stoppene underveis
 * nås likevel bare til fots, uansett hva resten av ruta bruker. Streken sa
 * ingenting om det.
 *
 * Reisemåten er valgt én gang for hele turen — det finnes ikke noe begrep om
 * «denne etappen med tog, den neste med buss» i denne planleggeren. Det eneste
 * som bryter mønsteret, er en fottur: `reachedOnFoot` er den samme sjekken som
 * «Nås med»-linja i StopDrawer bruker.
 *
 * Definisjonen bor her, ikke inne i 3D-kartet, fordi to steder trenger den:
 * ikonene langs streken og forklaringen under kartet. Se SERVICE_MARKS i
 * services.ts for samme begrunnelse med tjenestemerkene.
 */

export type VehicleGlyphKey = "tog" | "buss" | "bobil" | "bil" | "fot";

export interface VehicleGlyph {
  key: VehicleGlyphKey;
  icon: LucideIcon;
  color: string;
}

/*
 * `motorsykkel` har intet eget ikon i lucide-react — verken en motorsykkel
 * eller noe som ligner. Den deler bilikonet. Det er en reell mangel i
 * ikonsettet appen bruker, ikke en forglemmelse, og et bedre ikon kan legges
 * til den dagen et passer dukker opp i biblioteket.
 */
export const VEHICLE_GLYPHS: VehicleGlyph[] = [
  { key: "tog", icon: TrainFront, color: theme.color.fjord },
  { key: "buss", icon: Bus, color: theme.color.fjordMuted },
  { key: "bobil", icon: Caravan, color: theme.color.accent },
  { key: "bil", icon: CarFront, color: theme.color.accent },
  { key: "fot", icon: Footprints, color: theme.color.moss },
];

const GLYPH_BY_KEY: Record<VehicleGlyphKey, VehicleGlyph> = Object.fromEntries(
  VEHICLE_GLYPHS.map((glyph) => [glyph.key, glyph]),
) as Record<VehicleGlyphKey, VehicleGlyph>;

/**
 * Nås stoppet til fots, uansett hva resten av turen bruker?
 *
 * Samme spørsmål som «Nås med»-linja i StopDrawer svarer på: har stoppet en
 * fottur (`stop.hike`), er det trailheaden — ikke stoppet selv — kjøretøyet
 * når fram til. `modes` som bare inneholder «fottur» dekker stopp uten en
 * egen `Hike`, men som likevel bare nås gående.
 */
export function reachedOnFoot(stop: Stop): boolean {
  const onlyFoot = (modes: Mode[]) => modes.length === 1 && modes[0] === "fottur";
  return Boolean(stop.hike) || onlyFoot(stop.modes);
}

/** Hvilket ikon etappen inn til `toStop` skal vise. */
export function vehicleGlyphKeyFor(
  toStop: Stop,
  mode: TravelMode,
  vehicle: VehicleKind,
): VehicleGlyphKey {
  if (reachedOnFoot(toStop)) return "fot";
  if (mode === "tog") return "tog";
  if (mode === "buss") return "buss";
  /*
   * Kollektivt må stå FØR bil-reserven, ellers tegner den bil.
   *
   * Reserven her er `vehicle === "bobil" ? "bobil" : "bil"`, og en
   * funksjonskropp dekkes ikke av noen `Record`-uttømmingssjekk — så et nytt
   * reisemåtevalg faller gjennom til bil uten en type-feil noe sted.
   * Symptomet ville vært en kollektivtur som tegner en *bil* på hver etappe i
   * begge kartene, og en tegnforklaring under kartet som sier «med bil» under
   * en tur uten bil i det hele tatt.
   *
   * Ikonet velges av stoppet, ikke av valget: har stoppet jernbane, er
   * etappen tog — samme utledning som `legMode` gjør på serversiden.
   */
  if (mode === "kollektiv") return toStop.modes.includes("tog") ? "tog" : "buss";
  return vehicle === "bobil" ? "bobil" : "bil";
}

/** Samme som `vehicleGlyphKeyFor`, men gir ikonet og fargen direkte. */
export function vehicleGlyphFor(toStop: Stop, mode: TravelMode, vehicle: VehicleKind): VehicleGlyph {
  return GLYPH_BY_KEY[vehicleGlyphKeyFor(toStop, mode, vehicle)];
}
